import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import {useActivityModalControls} from 'llm/deep-research/ActivityModal';
import {
  DeepResearchChatInput,
  useDeepResearchChatInputControls,
} from 'llm/deep-research/DeepResearchChatInput';
import {DeepResearchEmptyState} from 'llm/deep-research/DeepResearchEmptyState';
import {
  DeepResearchMessageThread,
  useDeepResearchMessageThread,
} from 'llm/deep-research/DeepResearchMessageThread';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

export interface DeepResearchChatProps {
  chatId: string | null;
  onCreateChat: (chatId: string) => void;
  onRequestNewChat: () => void;
}

export function DeepResearchChat({
  chatId,
  onCreateChat,
  onRequestNewChat,
}: DeepResearchChatProps): JSX.Element {
  const {
    inputText,
    setInputText,
    publicDataEnabled,
    setPublicDataEnabled,
    emailNotificationEnabled,
    setEmailNotificationEnabled,
  } = useDeepResearchChatInputControls();
  const {messages, progressData, isSubmitting, sendMessage, clearChat} = useDeepResearchMessageThread({
    chatId,
    onCreateChat,
    onRequestNewChat,
  });
  const {openActivityModal} = useActivityModalControls();

  const handleSubmitMessage = useCallback(async () => {
    if (!inputText.trim() || isSubmitting) {
      return;
    }

    setInputText('');
    await sendMessage(inputText, publicDataEnabled, emailNotificationEnabled);
  }, [inputText, isSubmitting, publicDataEnabled, emailNotificationEnabled, sendMessage, setInputText]);

  const appendInputText = useCallback(
    (newMessage: string) => {
      if (newMessage.trim()) {
        setInputText(inputText + (inputText.length > 0 ? ' ' : '') + newMessage.trim());
      }
    },
    [inputText, setInputText]
  );

  return (
    <div className={css(styles.chatContainer)}>
      <div className={css(styles.messageThreadContainer)}>
        {
          // If a chat is persisted, render from DeepResearchMessageThread as this handles loading state.
          !chatId && messages.length === 0 ? (
            <DeepResearchEmptyState appendInputText={appendInputText} />
          ) : (
            <DeepResearchMessageThread
              messages={messages}
              progressData={progressData}
              openActivityModal={openActivityModal}
            />
          )
        }
      </div>
      <div className={css(styles.inputSection)}>
        {chatId && (
          <div className={css(styles.clearChatContainer)}>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.SMALL}
              text="New Chat"
              onClick={clearChat}
            />
          </div>
        )}
        <DeepResearchChatInput
          inputText={inputText}
          setInputText={setInputText}
          publicDataEnabled={publicDataEnabled}
          setPublicDataEnabled={setPublicDataEnabled}
          emailNotificationEnabled={emailNotificationEnabled}
          setEmailNotificationEnabled={setEmailNotificationEnabled}
          onSubmit={handleSubmitMessage}
          inputDisabled={isSubmitting}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  chatContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxWidth: '800px',
    width: '100%',
    margin: '0 auto',
  },
  messageThreadContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: '16px',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  inputSection: {
    position: 'relative',
  },
  clearChatContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '8px 0',
  },
});
